import { useState, useEffect, useCallback } from 'react'
import Swal from 'sweetalert2'
import {
  obtenerConfiguracionRegistro,
  obtenerPlanesPublicos,
  obtenerPlanesPorTipo,
  obtenerDistritos,
  obtenerEntidadesExternas,
  validarDisponibilidad,
  crearSolicitudRegistro
} from '../services/registroClienteApi'

/**
 * Hook especializado para el registro de clientes
 * NO reutiliza código existente siguiendo las especificaciones
 */
const useRegistroCliente = (onBack) => {
  // Estado principal del formulario
  const [datosFormulario, setDatosFormulario] = useState({
    nombre: '',
    apellidos: '',
    dni: '',
    fecha_nacimiento: '',
    email: '',
    telefono: '',
    direccion: '',
    distrito: '',
    ciudad: '',
    tipo_plan: '',
    subtipo_plan: '',
    id_entidad_externa: '',
    nombre_contacto_emergencia: '',
    telefono_contacto_emergencia: '',
    relacion_contacto: ''
  })

  // Estados de la interfaz
  const [paso, setPaso] = useState(1) // 1: Selección de plan, 2: Formulario completo
  const [enviando, setEnviando] = useState(false)
  const [cargandoDatos, setCargandoDatos] = useState(false)
  const [aceptoTerminos, setAceptoTerminos] = useState(false)

  // Datos desde el backend
  const [opcionesPlanesConfig, setOpcionesPlanesConfig] = useState([])
  const [planesCompletos, setPlanesCompletos] = useState([])
  const [planSeleccionado, setPlanSeleccionado] = useState(null)
  const [distritos, setDistritos] = useState([])
  const [entidadesExternas, setEntidadesExternas] = useState([])

  // Estados de validación
  const [erroresValidacion, setErroresValidacion] = useState({})
  const [validandoDisponibilidad, setValidandoDisponibilidad] = useState(false)

  /**
   * Cargar configuración inicial de registro
   */
  const cargarConfiguracionInicial = useCallback(async () => {
    try {
      setCargandoDatos(true)

      // Cargar configuración de registro, distritos y entidades externas en paralelo
      const [configuracion, distritosData, entidadesData] = await Promise.all([
        obtenerConfiguracionRegistro(),
        obtenerDistritos(),
        obtenerEntidadesExternas()
      ])

      if (configuracion.length > 0) {
        const config = configuracion[0]
        if (config.opciones_planes) {
          const opciones = typeof config.opciones_planes === 'string'
            ? JSON.parse(config.opciones_planes)
            : config.opciones_planes
          setOpcionesPlanesConfig(opciones)
        }
      }

      setDistritos(distritosData || [])
      setEntidadesExternas(entidadesData || [])

    } catch (error) {
      console.error('Error cargando configuración inicial:', error)
      await Swal.fire({
        title: 'Error',
        text: 'No se pudo cargar la configuración inicial. Por favor, recarga la página.',
        icon: 'error',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#1D44D1'
      })
    } finally {
      setCargandoDatos(false)
    }
  }, [])

  /**
   * Cargar planes específicos por tipo
   */
  const cargarPlanesPorTipo = useCallback(async (tipoPlan) => {
    try {
      setCargandoDatos(true)
      const planes = await obtenerPlanesPorTipo(tipoPlan)
      setPlanesCompletos(planes || [])
    } catch (error) {
      console.error('Error cargando planes por tipo:', error)
      setPlanesCompletos([])
    } finally {
      setCargandoDatos(false)
    }
  }, [])

  /**
   * Manejar selección de tipo de plan
   */
  const manejarSeleccionTipoPlan = useCallback(async (tipoPlan) => {
    setDatosFormulario(prev => ({
      ...prev,
      tipo_plan: tipoPlan,
      subtipo_plan: ''
    }))

    if (tipoPlan) {
      await cargarPlanesPorTipo(tipoPlan)
    } else {
      setPlanesCompletos([])
    }
  }, [cargarPlanesPorTipo])

  /**
   * Manejar selección de plan específico
   */
  const manejarSeleccionPlanEspecifico = useCallback((codigoPlan) => {
    const plan = planesCompletos.find(p => p.codigo === codigoPlan)
    setPlanSeleccionado(plan)
    setDatosFormulario(prev => ({
      ...prev,
      subtipo_plan: codigoPlan
    }))
  }, [planesCompletos])

  /**
   * Avanzar al siguiente paso
   */
  const avanzarSiguientePaso = useCallback(() => {
    if (paso === 1 && datosFormulario.tipo_plan && datosFormulario.subtipo_plan) {
      setPaso(2)
    }
  }, [paso, datosFormulario.tipo_plan, datosFormulario.subtipo_plan])

  /**
   * Volver al paso anterior
   */
  const volverPasoAnterior = useCallback(() => {
    if (paso === 2) {
      setPaso(1)
    } else {
      onBack()
    }
  }, [paso, onBack])

  /**
   * Manejar cambios en los campos del formulario
   */
  const manejarCambioInput = useCallback((evento) => {
    const { name, value } = evento.target

    setDatosFormulario(prev => ({
      ...prev,
      [name]: value
    }))

    // Limpiar error de validación para este campo
    if (erroresValidacion[name]) {
      setErroresValidacion(prev => ({
        ...prev,
        [name]: null
      }))
    }
  }, [erroresValidacion])

  /**
   * Validar disponibilidad de DNI y email
   */
  const validarDisponibilidadDatos = useCallback(async (dni, email) => {
    if (!dni && !email) return true

    try {
      setValidandoDisponibilidad(true)
      const resultado = await validarDisponibilidad(dni, email)

      if (!resultado.disponible) {
        await Swal.fire({
          title: 'Datos ya registrados',
          text: resultado.mensaje,
          icon: 'warning',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#1D44D1'
        })
        return false
      }

      return true
    } catch (error) {
      console.error('Error validando disponibilidad:', error)
      return true // Continuar si hay error en la validación
    } finally {
      setValidandoDisponibilidad(false)
    }
  }, [])

  /**
   * Validar formulario completo
   */
  const validarFormulario = useCallback(() => {
    const errores = {}

    // Validaciones requeridas
    if (!datosFormulario.nombre.trim()) errores.nombre = 'El nombre es requerido'
    if (!datosFormulario.apellidos.trim()) errores.apellidos = 'Los apellidos son requeridos'
    if (!datosFormulario.dni.trim()) errores.dni = 'El DNI es requerido'
    if (!datosFormulario.email.trim()) errores.email = 'El email es requerido'
    if (!datosFormulario.telefono.trim()) errores.telefono = 'El teléfono es requerido'
    if (!datosFormulario.direccion.trim()) errores.direccion = 'La dirección es requerida'
    if (!datosFormulario.distrito) errores.distrito = 'El distrito es requerido'
    if (!datosFormulario.ciudad.trim()) errores.ciudad = 'La ciudad es requerida'
    if (!datosFormulario.fecha_nacimiento) errores.fecha_nacimiento = 'La fecha de nacimiento es requerida'

    // Validaciones de contacto de emergencia
    if (!datosFormulario.nombre_contacto_emergencia.trim()) {
      errores.nombre_contacto_emergencia = 'El nombre del contacto de emergencia es requerido'
    }
    if (!datosFormulario.telefono_contacto_emergencia.trim()) {
      errores.telefono_contacto_emergencia = 'El teléfono del contacto de emergencia es requerido'
    }
    if (!datosFormulario.relacion_contacto) {
      errores.relacion_contacto = 'La relación del contacto de emergencia es requerida'
    }

    // Validación de entidad externa (SOLO si tipo_plan es EXTERNO)
    if (datosFormulario.tipo_plan === 'EXTERNO' && !datosFormulario.id_entidad_externa) {
      errores.id_entidad_externa = 'Debe seleccionar una entidad externa'
    }

    // Validaciones de formato
    if (datosFormulario.email && !/\S+@\S+\.\S+/.test(datosFormulario.email)) {
      errores.email = 'El formato del email no es válido'
    }

    if (datosFormulario.dni && datosFormulario.dni.length !== 8) {
      errores.dni = 'El DNI debe tener 8 dígitos'
    }

    // Validación de términos
    if (!aceptoTerminos) {
      errores.terminos = 'Debe aceptar los términos y condiciones'
    }

    setErroresValidacion(errores)
    return Object.keys(errores).length === 0
  }, [datosFormulario, aceptoTerminos])

  /**
   * Enviar solicitud de registro
   */
  const enviarSolicitudRegistro = useCallback(async (evento) => {
    evento.preventDefault()

    // Validar formulario
    if (!validarFormulario()) {
      await Swal.fire({
        title: 'Formulario incompleto',
        text: 'Por favor, completa todos los campos requeridos.',
        icon: 'warning',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#1D44D1'
      })
      return
    }

    // Validar disponibilidad de datos
    const disponible = await validarDisponibilidadDatos(datosFormulario.dni, datosFormulario.email)
    if (!disponible) return

    try {
      setEnviando(true)

      // Crear solicitud
      const resultado = await crearSolicitudRegistro(datosFormulario)

      // Mostrar confirmación
      await Swal.fire({
        title: '¡Solicitud Enviada!',
        html: `
          <div class="text-left space-y-3">
            <p>Tu solicitud de registro ha sido enviada exitosamente.</p>
            <div class="bg-blue-50 p-4 rounded-lg">
              <h4 class="font-semibold text-blue-800 mb-2">Próximos pasos:</h4>
              <ul class="text-sm text-blue-700 space-y-1">
                <li>• Nuestro equipo revisará tu solicitud</li>
                <li>• Te contactaremos en 24-48 horas</li>
                <li>• Recibirás un email con el estado de tu solicitud</li>
              </ul>
            </div>
            <p class="text-sm text-gray-600">
              <strong>Código de referencia:</strong> ${resultado.solicitud?.codigo || 'N/A'}
            </p>
          </div>
        `,
        icon: 'success',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#1D44D1'
      })

      // Volver a la pantalla anterior
      onBack()

    } catch (error) {
      console.error('Error enviando solicitud:', error)

      await Swal.fire({
        title: 'Error',
        text: error.message || 'Hubo un problema al enviar tu solicitud. Por favor, inténtalo nuevamente.',
        icon: 'error',
        confirmButtonText: 'Intentar de nuevo',
        confirmButtonColor: '#1D44D1'
      })
    } finally {
      setEnviando(false)
    }
  }, [datosFormulario, aceptoTerminos, validarFormulario, validarDisponibilidadDatos, onBack])

  // Cargar datos iniciales al montar el componente
  useEffect(() => {
    cargarConfiguracionInicial()
  }, [cargarConfiguracionInicial])

  return {
    // Estado del formulario
    datosFormulario,
    paso,
    enviando,
    cargandoDatos,
    aceptoTerminos,
    setAceptoTerminos,

    // Datos de referencia
    opcionesPlanesConfig,
    planesCompletos,
    planSeleccionado,
    distritos,
    entidadesExternas,

    // Estados de validación
    erroresValidacion,
    validandoDisponibilidad,

    // Funciones de navegación
    avanzarSiguientePaso,
    volverPasoAnterior,

    // Funciones de manejo de datos
    manejarCambioInput,
    manejarSeleccionTipoPlan,
    manejarSeleccionPlanEspecifico,
    enviarSolicitudRegistro
  }
}

export default useRegistroCliente